import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Clock, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import axios from 'axios';
import { toast } from 'sonner';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const apiUrl = process.env.REACT_APP_BACKEND_URL;
      await axios.post(`${apiUrl}/api/contact`, formData);
      toast.success('Thank you! We will contact you soon.');
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <section className="pt-32 pb-16 bg-gradient-to-br from-[#E0F2FE] via-white to-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}>
            <h1 className="text-5xl md:text-6xl font-bold text-[#0F172A] mb-6">
              Get in Touch
            </h1>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Have questions about your taxes? We're here to help guide you through
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}>
              <h2 className="text-3xl font-bold text-[#0F172A] mb-8">
                Contact Information
              </h2>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#E0F2FE] rounded-lg flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-[#005782]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#0F172A] mb-1">Phone</h3>
                    <a href="tel:+14377570478" className="text-slate-600 hover:text-[#005782]">
                      +1 (437) 757-0478
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#E0F2FE] rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-[#005782]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#0F172A] mb-1">Email</h3>
                    <a href="mailto:info@vktax.ca" className="text-slate-600 hover:text-[#005782]">
                      info@vktax.ca
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#E0F2FE] rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-[#005782]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#0F172A] mb-1">Office Address</h3>
                    <p className="text-slate-600">20 Bay St. 11th floor</p>
                    <p className="text-slate-600">Toronto, ON M5J 2N8</p>
                    <p className="text-slate-600">Canada</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#E0F2FE] rounded-lg flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-[#005782]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#0F172A] mb-1">Business Hours</h3>
                    <p className="text-slate-600">Monday - Friday: 9:00 AM - 7:00 PM</p>
                    <p className="text-slate-600">Saturday: 10:00 AM - 5:00 PM</p>
                    <p className="text-slate-600">Sunday: Closed</p>
                  </div>
                </div>
              </div>

              <div className="mt-12">
                <h3 className="text-xl font-semibold text-[#0F172A] mb-4">Follow Us</h3>
                <div className="flex items-center gap-3">
                  {/* LinkedIn */}
                  <a
                    href="https://www.linkedin.com/in/varshil-khatri/"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-linkedin-link"
                    aria-label="Connect on LinkedIn"
                    className="w-12 h-12 rounded-xl bg-[#0077B5] flex items-center justify-center transition-all duration-300 hover:scale-110 shadow-lg">
                    <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                    </svg>
                  </a>

                  {/* Instagram */}
                  <a
                    href="https://www.instagram.com/vktaxguide/"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-instagram-link"
                    aria-label="Follow us on Instagram"
                    className="w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-300 hover:scale-110 shadow-lg"
                    style={{
                      background:
                        'linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)',
                    }}>
                    <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.058-1.28.073-1.689.073-4.948 0-3.259-.015-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
                    </svg>
                  </a>

                  {/* WhatsApp */}
                  <a
                    href="https://wa.me/14377570478?text=Hi%20VK%20Tax%20Guide!%20I%20would%20like%20to%20know%20more%20about%20your%20tax%20filing%20services."
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-whatsapp-link"
                    aria-label="Message us on WhatsApp"
                    className="w-12 h-12 rounded-xl bg-[#25D366] flex items-center justify-center transition-all duration-300 hover:scale-110 shadow-lg">
                    <svg className="w-6 h-6 text-white" viewBox="0 0 32 32" fill="currentColor">
                      <path d="M16 0C7.164 0 0 7.164 0 16c0 2.825.738 5.488 2.031 7.794L0 32l8.394-2.031C10.7 31.263 13.362 32 16 32c8.836 0 16-7.164 16-16S24.836 0 16 0zm0 29.5c-2.538 0-4.95-.7-7-1.913l-.506-.3-5.231 1.263 1.263-5.231-.3-.506C3.2 20.95 2.5 18.538 2.5 16 2.5 8.544 8.544 2.5 16 2.5S29.5 8.544 29.5 16 23.456 29.5 16 29.5z" />
                      <path d="M22.744 18.756c-.394-.2-2.331-1.156-2.694-1.288-.362-.131-.625-.2-.887.2-.263.4-1.019 1.288-1.25 1.55-.231.263-.462.3-.856.1-.394-.2-1.663-.612-3.169-1.956-1.169-1.044-1.956-2.331-2.187-2.725-.231-.394-.025-.606.175-.8.181-.175.394-.462.594-.694.2-.231.263-.394.394-.656.131-.263.069-.494-.031-.694-.1-.2-.887-2.138-1.219-2.925-.325-.769-.656-.669-.887-.681-.231-.012-.494-.012-.756-.012s-.694.1-1.056.494c-.362.394-1.381 1.35-1.381 3.294s1.412 3.819 1.612 4.081c.2.263 2.825 4.313 6.844 6.05.956.413 1.7.656 2.281.838.956.306 1.831.263 2.519.163.769-.113 2.331-.956 2.656-1.881.325-.925.325-1.719.231-1.881-.094-.163-.356-.263-.75-.463z" />
                    </svg>
                  </a>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}>
              <div className="bg-white rounded-2xl p-8 shadow-xl border border-slate-100">
                <h2 className="text-3xl font-bold text-[#0F172A] mb-6">
                  Send Us a Message
                </h2>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Full Name *
                    </label>
                    <Input
                      data-testid="contact-name-input"
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="bg-white border-slate-200 focus:border-[#005782] focus:ring-2 focus:ring-[#005782]/20 rounded-lg h-12"
                      placeholder="John Doe"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Email Address *
                    </label>
                    <Input
                      data-testid="contact-email-input"
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="bg-white border-slate-200 focus:border-[#005782] focus:ring-2 focus:ring-[#005782]/20 rounded-lg h-12"
                      placeholder="john@example.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Phone Number *
                    </label>
                    <Input
                      data-testid="contact-phone-input"
                      type="tel"
                      required
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="bg-white border-slate-200 focus:border-[#005782] focus:ring-2 focus:ring-[#005782]/20 rounded-lg h-12"
                      placeholder="+1 (123) 456-7890"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Message *
                    </label>
                    <Textarea
                      data-testid="contact-message-input"
                      required
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      className="bg-white border-slate-200 focus:border-[#005782] focus:ring-2 focus:ring-[#005782]/20 rounded-lg min-h-32"
                      placeholder="Tell us about your tax needs..."
                    />
                  </div>

                  <Button
                    data-testid="contact-submit-btn"
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-[#005782] text-white hover:bg-[#01435c] rounded-lg px-8 py-4 text-lg font-medium transition-all shadow-lg hover:shadow-xl active:scale-95 flex items-center justify-center gap-2">
                    {isSubmitting ? (
                      'Sending...'
                    ) : (
                      <>
                        Send Message
                        <Send className="w-5 h-5" />
                      </>
                    )}
                  </Button>
                </form>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Contact;